import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Wallet,
  HandCoins,
  Clock3,
  ShieldCheck,
  Check,
  BadgeIndianRupee,
  Bike,
  Smartphone,
  IdCard,
} from "lucide-react";
import { useSEO } from "../lib/seo";
import { Reveal } from "../components/site/Reveal";

const EMAIL = "hello@getfetcher.in";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONEY = [
  {
    n: "01",
    icon: BadgeIndianRupee,
    title: "₹30 flat, every delivery",
    body: "You earn a flat ₹30 delivery fee on every completed order. Short trip or long — the fee is yours.",
  },
  {
    n: "02",
    icon: HandCoins,
    title: "Keep 100% of tips",
    body: "Every rupee a customer tips goes straight to you. We never take a cut of your tips.",
  },
  {
    n: "03",
    icon: Wallet,
    title: "Withdraw anytime to UPI",
    body: "No weekly payout waits. Move your balance to your UPI whenever you want.",
  },
  {
    n: "04",
    icon: ShieldCheck,
    title: "No commission, no targets",
    body: "We don't charge commission and we don't set daily targets. Ride when it suits you.",
  },
];

const REQUIREMENTS = [
  { icon: IdCard, label: "18 years or older" },
  { icon: Smartphone, label: "An Android smartphone" },
  { icon: Bike, label: "A two-wheeler or bicycle" },
  { icon: ShieldCheck, label: "A valid government ID" },
];

const VEHICLES = ["Two-wheeler (bike/scooter)", "Bicycle", "Other"];

export default function Riders() {
  useSEO({
    title: "Become a Fetcher Rider — Earn ₹30 flat per delivery, 100% yours",
    description:
      "Ride with Fetcher. Earn a flat ₹30 on every delivery, keep 100% of your tips, withdraw anytime to UPI. No commission, no target pressure. Sign up today.",
    path: "/riders",
  });

  const [form, setForm] = useState({ name: "", phone: "", city: "", vehicle: VEHICLES[0] });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || form.phone.replace(/\D/g, "").length < 8 || !form.city.trim()) {
      setError("Please fill in your name, a valid phone number and your city.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/riders/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Something went wrong. Please try again.");
      }
      setSubmitted(true);
    } catch (err) {
      setError(typeof err.message === "string" ? err.message : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToForm = () => {
    const el = document.getElementById("rider-signup");
    if (window.__lenis && el) window.__lenis.scrollTo(el, { offset: -80 });
    else if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  // Jump to the form when arriving via /riders#rider-signup (e.g. homepage CTA)
  useEffect(() => {
    if (window.location.hash === "#rider-signup") {
      const t = setTimeout(scrollToForm, 400);
      return () => clearTimeout(t);
    }
  }, []);

  return (
    <main data-testid="riders-page" className="bg-white">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#3A2E7E] pt-28 text-white lg:pt-36">
        <div className="pointer-events-none absolute -right-24 top-10 h-96 w-96 rounded-full bg-[#E86E28]/25 blur-3xl" />
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-8 px-5 pb-16 sm:px-8 lg:grid-cols-2 lg:pb-24">
          <div>
            <Reveal>
              <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-[#E86E28]">
                Ride with Fetcher
              </span>
            </Reveal>
            <Reveal delay={0.1}>
              <h1 className="mt-5 text-5xl font-extrabold leading-[1.05] tracking-tight sm:text-6xl">
                Earn on every delivery. <span className="text-[#E86E28]">100% yours.</span>
              </h1>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-lg text-base font-medium leading-relaxed text-white/75 sm:text-lg">
                No commission. No target pressure. Keep every rupee of the delivery fee and every rupee of the tip.
                Withdraw anytime to your UPI.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <button
                type="button"
                onClick={scrollToForm}
                data-testid="riders-hero-cta"
                className="group mt-8 inline-flex items-center gap-3 rounded-full bg-[#E86E28] px-8 py-4 text-lg font-extrabold text-white shadow-[0_14px_40px_rgba(232,110,40,0.45)] transition-all duration-300 hover:-translate-y-1 hover:bg-[#D95A17]"
              >
                Start earning
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </button>
            </Reveal>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="relative flex justify-center lg:justify-end"
          >
            <div className="absolute h-72 w-72 rounded-full bg-[#E86E28]/25 blur-3xl sm:h-96 sm:w-96" />
            <img
              src="/mascot-b.webp"
              alt="Fetcher mascot riding an orange delivery scooter"
              loading="eager"
              width="520"
              height="520"
              className="fetch-float relative w-72 max-w-full drop-shadow-[0_30px_50px_rgba(0,0,0,0.35)] sm:w-96 lg:w-[34rem]"
            />
          </motion.div>
        </div>
      </section>

      {/* HOW THE MONEY WORKS */}
      <section className="bg-white py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#D95A17]">How the money works</span>
            <h2 className="mt-3 max-w-2xl text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
              Your earnings, kept simple and honest.
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {MONEY.map((m, i) => (
              <Reveal
                key={m.n}
                delay={i * 0.08}
                data-testid={`riders-money-${m.n}`}
                className="group flex flex-col rounded-[1.25rem] border border-slate-100 bg-white p-6 shadow-[0_8px_30px_rgba(15,23,42,0.06)] transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_20px_50px_rgba(58,46,126,0.18)]"
              >
                <div className="flex items-center justify-between">
                  <span className="grid h-12 w-12 place-items-center rounded-2xl bg-[#EEEAFB] text-[#3A2E7E] transition-colors group-hover:bg-[#3A2E7E] group-hover:text-white">
                    <m.icon className="h-5 w-5" />
                  </span>
                  <span className="text-3xl font-black tracking-tighter text-slate-100 transition-colors group-hover:text-[#3A2E7E]">
                    {m.n}
                  </span>
                </div>
                <h3 className="mt-6 text-xl font-extrabold tracking-tight text-slate-900">{m.title}</h3>
                <p className="mt-2 text-sm font-medium leading-relaxed text-slate-500">{m.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* REQUIREMENTS */}
      <section className="bg-[#0B0F19] py-24 text-white lg:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-[1fr_1.2fr] lg:items-center">
            <Reveal>
              <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#E86E28]">What you need</span>
              <h2 className="mt-3 text-4xl font-extrabold tracking-tight sm:text-5xl">
                Getting started takes minutes.
              </h2>
              <p className="mt-4 max-w-md text-base font-medium leading-relaxed text-white/60">
                If you have these four things, you're ready to ride with Fetcher.
              </p>
            </Reveal>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {REQUIREMENTS.map((r, i) => (
                <Reveal
                  key={r.label}
                  delay={i * 0.08}
                  data-testid={`riders-req-${i}`}
                  className="flex items-center gap-4 rounded-2xl bg-white/5 p-5 ring-1 ring-white/10"
                >
                  <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[#E86E28] text-white">
                    <r.icon className="h-5 w-5" />
                  </span>
                  <span className="text-base font-bold">{r.label}</span>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SIGN-UP FUNNEL */}
      <section id="rider-signup" className="bg-gradient-to-tr from-[#FFF7ED] via-[#FFEDD5] to-[#FFE4E6] py-24 lg:py-32">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <Reveal className="text-center">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#D95A17]">Rider sign-up</span>
            <h2 className="mt-3 text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
              Ready to keep every rupee?
            </h2>
            <p className="mx-auto mt-4 max-w-lg text-base font-medium leading-relaxed text-slate-600">
              Tell us a little about you and we'll help you get on the road with Fetcher.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            {submitted ? (
              <div
                data-testid="riders-success"
                className="mt-10 flex flex-col items-center rounded-[1.5rem] border border-[#E86E28]/20 bg-white p-10 text-center shadow-[0_20px_60px_rgba(232,110,40,0.15)]"
              >
                <span className="grid h-16 w-16 place-items-center rounded-full bg-[#22c55e] text-white">
                  <Check className="h-8 w-8" strokeWidth={3} />
                </span>
                <h3 className="mt-5 text-2xl font-extrabold tracking-tight text-slate-900">Thanks, {form.name.split(" ")[0] || "rider"}!</h3>
                <p className="mt-2 max-w-md text-sm font-medium leading-relaxed text-slate-500">
                  We've received your details and our team will reach out soon to get you on the road.
                  Questions in the meantime? Email us at{" "}
                  <a href={`mailto:${EMAIL}?subject=Rider%20sign-up%20%E2%80%94%20Fetcher`} className="font-bold text-[#D95A17] underline">
                    {EMAIL}
                  </a>.
                </p>
                <button
                  type="button"
                  data-testid="riders-reset"
                  onClick={() => setSubmitted(false)}
                  className="mt-6 rounded-full bg-slate-900 px-6 py-3 text-sm font-extrabold text-white transition hover:-translate-y-0.5 hover:bg-black"
                >
                  Submit another
                </button>
              </div>
            ) : (
              <form
                data-testid="riders-form"
                onSubmit={onSubmit}
                className="mt-10 rounded-[1.5rem] border border-white/60 bg-white/80 p-6 shadow-[0_20px_60px_rgba(15,23,42,0.08)] backdrop-blur-sm sm:p-8"
              >
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <label className="flex flex-col gap-2">
                    <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Full name</span>
                    <input
                      data-testid="riders-input-name"
                      type="text"
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Ravi Kumar"
                      className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-base font-medium text-slate-900 outline-none transition focus:border-[#E86E28] focus:ring-2 focus:ring-[#E86E28]/20"
                    />
                  </label>
                  <label className="flex flex-col gap-2">
                    <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Phone number</span>
                    <input
                      data-testid="riders-input-phone"
                      type="tel"
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder="+91 98765 43210"
                      className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-base font-medium text-slate-900 outline-none transition focus:border-[#E86E28] focus:ring-2 focus:ring-[#E86E28]/20"
                    />
                  </label>
                  <label className="flex flex-col gap-2">
                    <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">City</span>
                    <input
                      data-testid="riders-input-city"
                      type="text"
                      value={form.city}
                      onChange={update("city")}
                      placeholder="Chennai"
                      className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-base font-medium text-slate-900 outline-none transition focus:border-[#E86E28] focus:ring-2 focus:ring-[#E86E28]/20"
                    />
                  </label>
                  <label className="flex flex-col gap-2">
                    <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Vehicle</span>
                    <select
                      data-testid="riders-input-vehicle"
                      value={form.vehicle}
                      onChange={update("vehicle")}
                      className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-base font-medium text-slate-900 outline-none transition focus:border-[#E86E28] focus:ring-2 focus:ring-[#E86E28]/20"
                    >
                      {VEHICLES.map((v) => (
                        <option key={v} value={v}>
                          {v}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                {error && (
                  <p data-testid="riders-error" className="mt-4 text-sm font-semibold text-red-600">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  data-testid="riders-submit"
                  disabled={submitting}
                  className="group mt-6 flex w-full items-center justify-center gap-3 rounded-full bg-[#E86E28] px-8 py-4 text-lg font-extrabold text-white shadow-[0_14px_40px_rgba(232,110,40,0.4)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#D95A17] disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {submitting ? "Sending…" : "Sign up as rider"}
                  {!submitting && <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />}
                </button>
                <p className="mt-4 flex items-center justify-center gap-2 text-xs font-medium text-slate-500">
                  <Clock3 className="h-3.5 w-3.5" /> We usually respond within 24 hours.
                </p>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </main>
  );
}
